import React, { useState, useEffect } from 'react';
import { __ } from './translations';
import { X, UserPlus, UserMinus, Upload } from 'lucide-react';

const CompanyModal = ({ company, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        // Company Information
        companyName: company?.title?.rendered || '',
        nip: company?.meta?._bms_nip || '',
        city: company?.meta?._bms_city || '',
        postalCode: company?.meta?._bms_postal_code || '',
        street: company?.meta?._bms_street || '',
        number: company?.meta?._bms_number || '',
        companyEmail: company?.meta?._bms_company_email || '',
        memberType: company?.meta?._bms_member_type || 'regular',

        // Recipient Information
        recipientName: company?.meta?._bms_recipient_name || '',
        recipientCity: company?.meta?._bms_recipient_city || '',
        recipientPostalCode: company?.meta?._bms_recipient_postal_code || '',
        recipientStreet: company?.meta?._bms_recipient_street || '',
        recipientNumber: company?.meta?._bms_recipient_number || '',
        recipientNip: company?.meta?._bms_recipient_nip || '',
        recipientEmail: company?.meta?._bms_recipient_email || ''
    });

    const [users, setUsers] = useState(company?.meta?._assigned_users || []);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [hasRecipient, setHasRecipient] = useState(!!company?.meta?._bms_recipient_name);
    const [resolutionFile, setResolutionFile] = useState(null);
    const [currentResolution, setCurrentResolution] = useState(company?.resolution_file || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUserAdd = () => {
        if (newUserEmail && !users.includes(newUserEmail)) {
            setUsers([...users, newUserEmail]);
            setNewUserEmail('');
        }
    };

    const handleUserRemove = (email) => {
        setUsers(users.filter(u => u !== email));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
    
        try {
            // Prepare company data
            const companyData = {
                companyName: formData.companyName,
                nip: formData.nip,
                city: formData.city,
                postalCode: formData.postalCode,
                street: formData.street,
                number: formData.number,
                companyEmail: formData.companyEmail,
                memberType: formData.memberType,
                recipientName: formData.recipientName,
                recipientCity: formData.recipientCity,
                recipientPostalCode: formData.recipientPostalCode,
                recipientStreet: formData.recipientStreet,
                recipientNumber: formData.recipientNumber,
                recipientNip: formData.recipientNip,
                recipientEmail: formData.recipientEmail,
                assignedUsers: users
            };
    
            let response;
            
            if (company?.id) {
                // Update existing company
                response = await fetch(`${boardMemberData.restURL}board-member/v1/companies/${company.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': boardMemberData.nonce
                    },
                    body: JSON.stringify({
                        company_data: companyData
                    })
                });
            } else {
                // Create new company
                response = await fetch(`${boardMemberData.restURL}board-member/v1/companies`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': boardMemberData.nonce
                    },
                    body: JSON.stringify({
                        company_data: companyData
                    })
                });
            }
    
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to save company data');
            }
    
            const result = await response.json();
            
            // Handle resolution file upload if present
            if (resolutionFile) {
                const formData = new FormData();
                formData.append('resolution', resolutionFile);
        
                const fileResponse = await fetch(
                    `${boardMemberData.restURL}board-member/v1/companies/${result.company_id}/resolution`, 
                    {
                        method: 'POST',
                        headers: {
                            'X-WP-Nonce': boardMemberData.nonce
                        },
                        body: formData
                    }
                );
    
                if (!fileResponse.ok) {
                    const fileError = await fileResponse.json();
                    throw new Error(fileError.message || 'Failed to upload resolution file');
                }
            }
    
            onSave();
        } catch (err) {
            setError(err.message);
            console.error('Error saving company:', err);
        } finally {
            setLoading(false);
        }
    };

    // Add this section to display current resolution file if it exists
  const ResolutionFileDisplay = () => {
    if (currentResolution) {
      return (
        <div className="flex items-center space-x-2 mt-2">
          <span className="text-sm text-gray-600">{__('Current file:')} </span>
          <a 
            href={currentResolution.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {currentResolution.name}
          </a>
        </div>
      );
    }
    return null;
  };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold">
                            {company ? __('Edit Company') : __('Add New Company')}
                        </h2>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {error && (
                        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-4">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Company Information */}
                        <section>
                            <h3 className="text-lg font-semibold mb-4">{__('Company Information')}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {__('Company Name')}
                                    </label>
                                    <input
                                        type="text"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {__('NIP')}
                                    </label>
                                    <input
                                        type="text"
                                        name="nip"
                                        value={formData.nip}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        required
                                        pattern="\d{10}"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {__('City')}
                                    </label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {__('Postal Code')}
                                    </label>
                                    <input
                                        type="text"
                                        name="postalCode"
                                        value={formData.postalCode}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        required
                                        pattern="\d{2}-\d{3}"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {__('Street')}
                                    </label>
                                    <input
                                        type="text"
                                        name="street"
                                        value={formData.street}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {__('Number')}
                                    </label>
                                    <input
                                        type="text"
                                        name="number"
                                        value={formData.number}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {__('Email')}
                                    </label>
                                    <input
                                        type="email"
                                        name="companyEmail"
                                        value={formData.companyEmail}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {__('Member Type')}
                                    </label>
                                    <select
                                        name="memberType"
                                        value={formData.memberType}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    >
                                        <option value="regular">{__('Regular Member')}</option>
                                        <option value="supporter">{__('Supporter')}</option>
                                    </select>
                                </div>
                            </div>
                        </section>

                        {/* Recipient Information Toggle */}
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="hasRecipient"
                                checked={hasRecipient}
                                onChange={(e) => setHasRecipient(e.target.checked)}
                                className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                            <label htmlFor="hasRecipient" className="text-sm font-medium text-gray-700">
                                {__('Different Recipient Information')}
                            </label>
                        </div>

                        {/* Recipient Information */}
                        {hasRecipient && (
                            <section>
                                <h3 className="text-lg font-semibold mb-4">{__('Recipient Information')}</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Add recipient fields similar to company fields */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {__('Recipient Name')}
                                        </label>
                                        <input
                                            type="text"
                                            name="recipientName"
                                            value={formData.recipientName}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {__('City')}
                                        </label>
                                        <input
                                            type="text"
                                            name="recipientCity"
                                            value={formData.recipientCity}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {__('Postal Code')}
                                        </label>
                                        <input
                                            type="text"
                                            name="recipientPostalCode"
                                            value={formData.recipientPostalCode}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {__('Street')}
                                        </label>
                                        <input
                                            type="text"
                                            name="recipientStreet"
                                            value={formData.recipientStreet}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {__('Building Number')}
                                        </label>
                                        <input
                                            type="text"
                                            name="recipientNumber"
                                            value={formData.recipientNumber}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {__('NIP')}
                                        </label>
                                        <input
                                            type="text"
                                            name="recipientNip"
                                            value={formData.recipientNip}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {__('Email')}
                                        </label>
                                        <input
                                            type="text"
                                            name="recipientEmail"
                                            value={formData.recipientEmail}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        />
                                    </div>
                                </div>
                            </section>
                        )}

                        {/* User Management */}
                        <section>
                            <h3 className="text-lg font-semibold mb-4">{__('User Management')}</h3>
                            <div className="space-y-4">
                                <div className="flex gap-2">
                                    <input
                                        type="email"
                                        value={newUserEmail}
                                        onChange={(e) => setNewUserEmail(e.target.value)}
                                        placeholder={__('Enter user email')}
                                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleUserAdd}
                                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        <UserPlus className="w-4 h-4 mr-2" />
                                        {__('Add User')}
                                    </button>
                                </div>

                                <ul className="space-y-2">
                                    {users.map((email, index) => (
                                        <li key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                                            <span>{email}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleUserRemove(email)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <UserMinus className="w-4 h-4" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </section>

                        {/* Resolution Upload */}
                        <section>
      <h3 className="text-lg font-semibold mb-4">{__('Company Resolution')}</h3>
      <div className="space-y-2">
        <ResolutionFileDisplay />
        <div className="flex items-center space-x-4">
          <input
            type="file"
            accept=".pdf"
            onChange={(e) => setResolutionFile(e.target.files[0])}
            className="hidden"
            id="resolution-upload"
          />
          <label
            htmlFor="resolution-upload"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
          >
            <Upload className="w-4 h-4 mr-2" />
            {currentResolution ? __('Change Resolution') : __('Upload Resolution')}
          </label>
          {resolutionFile && (
            <span className="text-sm text-gray-600">
              {resolutionFile.name}
            </span>
          )}
        </div>
      </div>
    </section>
                        {/* Form Actions */}
                        <div className="flex justify-end space-x-2 mt-6 border-t pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={loading}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                {__('Cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                  ${loading ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'} 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                            >
                                {loading && (
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                                {loading ? __('Saving...') : __('Save Changes')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}
export default CompanyModal